/**
 * Pricing Scenarios Component
 * Pricing decision cockpit for cost, sample quality, and plan simulation.
 */

import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  BarChart3,
  CircleDollarSign,
  Database,
  LineChart,
  Minus,
  ReceiptText,
  Scale,
  SlidersHorizontal,
  Users,
  type LucideIcon,
} from 'lucide-react';

import { Badge, Card } from '@/components/ui';
import { cn } from '@/lib/cn';
import type { CostSummary, PricingScenario, ReportsDashboard } from '@/lib/api/reports';
import {
  getBreakEvenPrice,
  getCostMixItems,
  perUser,
  windowToMonthly,
  type CostMixItem,
  type Tone,
} from '../pricingTerms';

interface PricingScenariosProps {
  scenarios: PricingScenario[];
  dashboard: ReportsDashboard;
  className?: string;
}

export function PricingScenarios({ scenarios, dashboard, className }: PricingScenariosProps) {
  const [selectedId, setSelectedId] = useState(scenarios[0]?.id || 'current');

  const selectedScenario = useMemo(
    () => scenarios.find((scenario) => scenario.id === selectedId) || scenarios[0],
    [scenarios, selectedId]
  );
  const currentScenario = useMemo(
    () => scenarios.find((scenario) => scenario.id === 'current') || scenarios[0],
    [scenarios]
  );

  const dateWindow = useMemo(
    () => ({
      days: dashboard.pricing_context.window_days,
      monthFactor: dashboard.pricing_context.month_factor,
      label: `${dashboard.date_range.start} ~ ${dashboard.date_range.end}`,
    }),
    [
      dashboard.date_range.end,
      dashboard.date_range.start,
      dashboard.pricing_context.month_factor,
      dashboard.pricing_context.window_days,
    ]
  );
  const monthlyProjection = selectedScenario?.monthly_projection;
  const currentMonthlyProjection = currentScenario?.monthly_projection;
  const sampleQuality = getSampleQuality(dashboard.summary);
  const observedRevenue = dashboard.pricing_context.observed_revenue;

  if (!selectedScenario || !monthlyProjection || !currentScenario || !currentMonthlyProjection) {
    return (
      <Card className={cn('p-8 text-center', className)}>
        <CircleDollarSign className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
        <p className="text-sm font-medium text-foreground">暂无定价方案数据</p>
      </Card>
    );
  }

  const { summary } = dashboard;
  const p75Cost = summary.cost_percentiles.p75 ?? 0;
  const p90Cost = summary.cost_percentiles.p90 ?? 0;
  const costMixItems = getCostMixItems(summary);
  const breakEvenPrice = getBreakEvenPrice(monthlyProjection.projected_cost, selectedScenario.refund_rate);
  const costSafetyLine = selectedScenario.plan_price_cny * (1 - selectedScenario.refund_rate);
  const topUserRisk = p90Cost / Math.max(0.01, selectedScenario.plan_price_cny);

  return (
    <div className={cn('space-y-5', className)}>
      <Card className="p-4">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-wrap gap-2">
            <StatusPill tone={sampleQuality.tone} icon={sampleQuality.icon} label={sampleQuality.label} />
            <StatusPill tone="profit" icon={Database} label="成本中心账本已接入" />
            <StatusPill tone="profit" icon={ReceiptText} label="真实收入退款已接入" />
            <StatusPill tone="warning" icon={SlidersHorizontal} label="模型中心重计价未接入" />
            <StatusPill tone="info" icon={Scale} label="后端月化已接入" />
          </div>

          <div className="grid w-full grid-cols-3 rounded-lg border border-border bg-surface-2 p-1 text-xs font-medium text-muted-foreground xl:w-auto">
            <button
              className="rounded-md bg-card px-3 py-2 text-primary shadow-elevation-1"
              type="button"
            >
              历史账本
            </button>
            <button className="px-3 py-2 text-muted-foreground/70" type="button" disabled>
              最新模型中心
            </button>
            <button className="px-3 py-2 text-muted-foreground/70" type="button" disabled>
              方案模拟
            </button>
          </div>
        </div>
      </Card>

      <section className="grid grid-cols-1 gap-4 xl:grid-cols-[1.15fr_0.85fr]">
        <div className="overflow-hidden rounded-lg border border-border bg-foreground text-background shadow-elevation-1">
          <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-4">
            <HeroStat
              label="当前方案月化利润"
              badge="非决策级"
              value={formatCurrency(currentMonthlyProjection.projected_profit)}
              detail={`原始窗口 ${formatCurrency(currentScenario.raw_projection.projected_profit)}`}
              tone={currentMonthlyProjection.projected_profit >= 0 ? 'profit' : 'danger'}
            />
            <HeroStat
              label="月化毛利率"
              badge="非决策级"
              value={formatPercent(currentMonthlyProjection.profit_margin)}
              detail={`原始窗口 ${formatPercent(currentScenario.raw_projection.profit_margin)}`}
              tone={getMarginTone(currentMonthlyProjection.profit_margin)}
            />
            <HeroStat
              label="样本用户"
              value={formatNumber(summary.total_users)}
              detail={`会话 ${formatNumber(summary.total_sessions)} 次`}
              tone="neutral"
            />
            <HeroStat
              label="月化人均成本"
              badge="后端口径"
              value={formatCurrency(windowToMonthly(summary.avg_cost_per_user_cny, dateWindow.monthFactor))}
              detail={`窗口人均 ${formatCurrency(summary.avg_cost_per_user_cny)}`}
              tone="neutral"
            />
          </div>
          <div className="border-t border-background/10 px-5 py-4 text-sm leading-6 text-background/75">
            样本来自 {dateWindow.label}，窗口 {dateWindow.days} 天；月化值使用后端 month_factor {dateWindow.monthFactor.toFixed(2)}，待必需 M11 账本可信度闸门完成前仍为 non-decision-grade。
          </div>
        </div>

        <Card className="p-5">
          <div className="mb-5 flex items-center justify-between gap-3">
            <div>
              <h4 className="text-sm font-semibold text-foreground">月化利润桥</h4>
              <p className="mt-1 text-xs text-muted-foreground">100 用户/月，收入 - 月化成本 - 退款；利润不作为决策级结论。</p>
            </div>
            <ReceiptText className="h-5 w-5 shrink-0 text-muted-foreground" />
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_auto_minmax(0,1fr)_auto_minmax(0,1fr)] sm:items-center">
            <BridgeValue label="收入" value={formatCurrency(currentMonthlyProjection.projected_revenue)} tone="profit" />
            <Minus className="hidden h-4 w-4 text-muted-foreground sm:block" />
            <BridgeValue label="月化成本" value={formatCurrency(currentMonthlyProjection.projected_cost)} tone="danger" />
            <Minus className="hidden h-4 w-4 text-muted-foreground sm:block" />
            <BridgeValue label="退款" value={formatCurrency(currentMonthlyProjection.projected_refund_loss)} tone="warning" />
            <span className="hidden text-center text-muted-foreground sm:block">=</span>
            <BridgeValue
              label="利润"
              value={formatCurrency(currentMonthlyProjection.projected_profit)}
              tone={currentMonthlyProjection.projected_profit >= 0 ? 'profit' : 'danger'}
            />
          </div>
        </Card>
      </section>

      <Card className="p-5">
        <PanelHeader
          icon={ReceiptText}
          title="实际收入与退款"
          description="与 Revenue / P&L 口径一致，收入按 purchase_date，退款按 refunded_at。"
        />
        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <ObservedRevenueMetric
            label="实际毛收入"
            value={formatCurrency(observedRevenue.gross_revenue_cny)}
            detail={`${formatNumber(observedRevenue.transaction_count)} 笔交易`}
            tone="profit"
          />
          <ObservedRevenueMetric
            label="实际退款"
            value={formatCurrency(observedRevenue.refund_cny)}
            detail={`${formatNumber(observedRevenue.refund_count)} 笔退款`}
            tone={observedRevenue.refund_cny > 0 ? 'warning' : 'neutral'}
          />
          <ObservedRevenueMetric
            label="实际净收入"
            value={formatCurrency(observedRevenue.net_revenue_cny)}
            detail={`${formatNumber(observedRevenue.paying_users)} 个付费用户`}
            tone={observedRevenue.net_revenue_cny >= 0 ? 'profit' : 'danger'}
          />
          <ObservedRevenueMetric
            label="实际退款率"
            value={
              observedRevenue.observed_refund_rate === null
                ? '无可计算'
                : formatPercent(observedRevenue.observed_refund_rate * 100)
            }
            detail={getRefundRateSourceLabel(currentScenario.refund_rate_source)}
            tone={observedRevenue.observed_refund_rate === null ? 'warning' : 'info'}
          />
        </div>
      </Card>

      <section className="grid grid-cols-1 gap-4 xl:grid-cols-[1fr_1fr_0.72fr]">
        <Card className="p-5">
          <PanelHeader
            icon={BarChart3}
            title="用户成本分布"
            description="用分位数判断重度用户风险，不只看平均值。"
          />
          <div className="mt-5 space-y-3">
            <DistributionRow
              label="P50 中位数"
              value={formatCurrency(summary.median_cost_cny)}
              percent={35}
              detail="50% 用户低于该窗口成本"
              tone="neutral"
            />
            <DistributionRow
              label="P75"
              value={formatCurrency(p75Cost)}
              percent={58}
              detail="75% 用户低于该窗口成本"
              tone="neutral"
            />
            <DistributionRow
              label="P90"
              value={formatCurrency(p90Cost)}
              percent={86}
              detail="重度用户风险线"
              tone={p90Cost > dashboard.config.member_price ? 'danger' : 'neutral'}
            />
            <DistributionRow
              label="平均值"
              value={formatCurrency(summary.avg_cost_per_user_cny)}
              percent={Math.min(100, (summary.avg_cost_per_user_cny / Math.max(0.01, p90Cost)) * 86)}
              detail="容易被高成本用户拉高"
              tone="warning"
            />
          </div>
          <div className="mt-5 rounded-lg border border-border bg-surface-2 px-3 py-2 text-xs leading-5 text-muted-foreground">
            样本可信度：<span className={cn('font-semibold', textToneStyles[sampleQuality.tone])}>{sampleQuality.label}</span>
            <span className="ml-2">样本 {summary.total_users} 人；建议 ≥30 人后再观察，≥100 人后再接近定价判断。</span>
          </div>
        </Card>

        <Card className="p-5">
          <PanelHeader
            icon={Database}
            title="成本构成"
            description="按账本总成本拆分；非零未分类成本会单独列出。"
          />
          <CostMixBar
            items={costMixItems}
            totalCost={summary.total_cost_cny}
          />
          <div className="mt-4 divide-y divide-border rounded-lg border border-border">
            {costMixItems.map((item) => (
              <CostMixRow
                key={item.label}
                label={item.label}
                amount={windowToMonthly(item.value, dateWindow.monthFactor)}
                share={getShare(item.value, summary.total_cost_cny)}
                perUser={windowToMonthly(perUser(item.value, summary.total_users), dateWindow.monthFactor)}
              />
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <PanelHeader icon={LineChart} title="辅助定价信号" description="当前数据能支持的直接观察。" />
          <div className="mt-5 space-y-3">
            <SignalRow
              label="保本月价"
              value={formatCurrency(breakEvenPrice)}
              detail="按选中方案月化成本和退款率估算"
              tone="info"
            />
            <SignalRow
              label="成本安全线"
              value={formatCurrency(costSafetyLine)}
              detail="价格扣除退款后的可承受成本"
              tone="neutral"
            />
            <SignalRow
              label="重度用户拖累"
              value={`${topUserRisk.toFixed(2)}x`}
              detail="P90 窗口成本 / 选中月价"
              tone={topUserRisk > 1 ? 'danger' : 'profit'}
            />
            <SignalRow
              label="盈利用户占比"
              value={formatPercent(summary.profitable_rate * 100)}
              detail={`${summary.profitable_users}/${summary.total_users} 个样本用户`}
              tone={summary.profitable_rate >= 0.5 ? 'profit' : 'warning'}
            />
          </div>
        </Card>
      </section>

      <Card className="p-5">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <PanelHeader
            icon={CircleDollarSign}
            title="方案对比"
            description="卡片展示后端月化 projection；底部保留原始窗口成本以避免口径混淆。"
          />
          <Badge variant="outline" className="shrink-0">
            基准：100 用户/月
          </Badge>
        </div>

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 2xl:grid-cols-4">
          {scenarios.map((scenario) => {
            const isSelected = selectedId === scenario.id;
            const normalized = scenario.monthly_projection;

            return (
              <button
                key={scenario.id}
                type="button"
                onClick={() => setSelectedId(scenario.id)}
                className={cn(
                  'flex min-h-[196px] min-w-0 flex-col rounded-lg border p-4 text-left transition duration-200 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background',
                  isSelected
                    ? 'border-primary bg-primary/10 shadow-elevation-1'
                    : 'border-border bg-card hover:bg-accent'
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h5 className="break-words text-sm font-semibold leading-5 text-foreground">{scenario.name}</h5>
                    <p className="mt-2 line-clamp-2 break-words text-xs leading-5 text-muted-foreground">{scenario.notes}</p>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      <Badge variant="secondary" className="text-[11px]">
                        {getScenarioRefundSourceLabel(scenario.refund_rate_source)}
                      </Badge>
                      <Badge variant="outline" className="text-[11px]">
                        {getServicePlaneLabel(scenario.included_service_plane)}
                      </Badge>
                    </div>
                  </div>
                  <ScenarioTrendIcon profit={normalized.projected_profit} />
                </div>

                <div className="mt-auto grid grid-cols-2 gap-2 pt-4">
                  <ScenarioMiniMetric label="月价" value={formatCurrency(scenario.plan_price_cny)} tone="neutral" />
                  <ScenarioMiniMetric
                    label="退款率"
                    value={formatPercent(scenario.refund_rate * 100)}
                    tone="warning"
                  />
                  <ScenarioMiniMetric
                    label="月化利润"
                    value={formatCurrency(normalized.projected_profit)}
                    tone={normalized.projected_profit >= 0 ? 'profit' : 'danger'}
                  />
                  <ScenarioMiniMetric
                    label="月化毛利"
                    value={formatPercent(normalized.profit_margin)}
                    tone={getMarginTone(normalized.profit_margin)}
                  />
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <ProjectionItem label="收入" value={formatCurrency(selectedScenario.raw_projection.projected_revenue)} variant="profit" />
          <ProjectionItem label="原始窗口成本" value={formatCurrency(selectedScenario.raw_projection.projected_cost)} variant="danger" />
          <ProjectionItem label="月化成本" value={formatCurrency(monthlyProjection.projected_cost)} variant="warning" />
          <ProjectionItem
            label="退款损失"
            value={formatCurrency(monthlyProjection.projected_refund_loss)}
            variant="warning"
            subtitle={`退费率 ${formatPercent(selectedScenario.refund_rate * 100)}`}
          />
          <ProjectionItem
            label="月化利润"
            value={formatCurrency(monthlyProjection.projected_profit)}
            variant={monthlyProjection.projected_profit >= 0 ? 'profit' : 'danger'}
          />
        </div>
      </Card>

      <Card className="border-warning/40 bg-warning/10 p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
          <div>
            <h4 className="text-sm font-semibold text-foreground">当前结论的边界</h4>
            <p className="mt-1 text-sm leading-6 text-foreground">
              本页 monthly 值已使用后端月化合同和真实收入退款；必需 M11 账本可信度闸门完成前，不得作为正式定价决策依据。
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}

function getSampleQuality(summary: CostSummary): { label: string; tone: Tone; icon: LucideIcon } {
  if (summary.total_users >= 100) return { label: '样本充足', tone: 'profit', icon: Users };
  if (summary.total_users >= 30) return { label: '样本观察中', tone: 'warning', icon: Users };
  return { label: '样本不足', tone: 'danger', icon: Users };
}

function getMarginTone(margin: number): Tone {
  if (margin >= 20) return 'profit';
  if (margin >= 0) return 'warning';
  return 'danger';
}

function getRefundRateSourceLabel(source: string) {
  if (source === 'observed') return '当前方案使用观测退款率';
  if (source === 'override') return '建模退款率';
  if (source === 'observed_unavailable') return '当前方案无可计算观测退款率';
  if (source === 'observed_out_of_range') return '当前方案观测退款率超出可用范围';
  return '未知退款率来源';
}

function getScenarioRefundSourceLabel(source: string) {
  if (source === 'observed') return '观测退款率';
  if (source === 'override') return '建模退款率';
  if (source === 'observed_unavailable') return '观测退款率不可用';
  if (source === 'observed_out_of_range') return '观测退款率超界';
  return '未知退款率';
}

function getServicePlaneLabel(plane: string) {
  if (plane === 'full') return '完整服务面';
  if (plane === 'lite') return '轻量服务面';
  if (plane === 'none') return '仅聊天';
  return '未知服务面';
}

function getShare(value: number, total: number) {
  return total > 0 ? (value / total) * 100 : 0;
}

function formatCurrency(value: number) {
  const sign = value < 0 ? '-' : '';
  return `${sign}¥${Math.abs(value).toFixed(2)}`;
}

function formatPercent(value: number) {
  return `${value.toFixed(1)}%`;
}

function formatNumber(value: number) {
  return value.toLocaleString('zh-CN');
}

function StatusPill({ icon: Icon, label, tone }: { icon: LucideIcon; label: string; tone: Tone }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium',
        pillToneStyles[tone]
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </span>
  );
}

function HeroStat({
  label,
  value,
  detail,
  tone,
  badge,
}: {
  label: string;
  value: string;
  detail: string;
  tone: Tone;
  badge?: string;
}) {
  return (
    <div className="border-b border-background/10 p-5 2xl:border-b-0 2xl:border-r last:2xl:border-r-0">
      <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-background/65">
        <span>{label}</span>
        {badge && <span className="rounded-sm bg-background/10 px-1.5 py-0.5 text-[11px] text-background/80">{badge}</span>}
      </div>
      <div className={cn('mt-3 break-words font-mono text-2xl font-semibold tracking-normal xl:text-3xl', heroToneStyles[tone])}>{value}</div>
      <div className="mt-2 break-words text-xs text-background/65">{detail}</div>
    </div>
  );
}

function PanelHeader({ icon: Icon, title, description }: { icon: LucideIcon; title: string; description: string }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h4 className="break-words text-sm font-semibold text-foreground">{title}</h4>
        <p className="mt-1 break-words text-xs leading-5 text-muted-foreground">{description}</p>
      </div>
      <Icon className="h-5 w-5 shrink-0 text-muted-foreground" />
    </div>
  );
}

function DistributionRow({
  label,
  value,
  percent,
  detail,
  tone,
}: {
  label: string;
  value: string;
  percent: number;
  detail: string;
  tone: Tone;
}) {
  return (
    <div className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-[minmax(6rem,8rem)_minmax(5rem,7rem)_minmax(0,1fr)] sm:items-center sm:gap-3">
      <div className="break-words text-muted-foreground">{label}</div>
      <div className={cn('rounded-md border bg-surface-2 px-2 py-1 text-right font-mono text-xs font-semibold tracking-normal', textToneStyles[tone])}>
        {value}
      </div>
      <div className="min-w-0">
        <div className="h-2 rounded-full bg-surface-3">
          <div className={cn('h-2 rounded-full', barToneStyles[tone])} style={{ width: `${Math.min(100, Math.max(4, percent))}%` }} />
        </div>
        <div className="mt-1 break-words text-xs text-muted-foreground">{detail}</div>
      </div>
    </div>
  );
}

function CostMixBar({ items, totalCost }: { items: CostMixItem[]; totalCost: number }) {
  return (
    <div className="mt-5">
      <div className="mb-2 flex items-center justify-between gap-3 text-xs font-medium text-muted-foreground">
        <span>账本总成本</span>
        <span className="font-mono tracking-normal">{formatCurrency(totalCost)}</span>
      </div>
      <div className="flex h-4 overflow-hidden rounded-md bg-surface-3">
        {items.map((item) => (
          <div
            key={item.label}
            className={barToneStyles[item.tone]}
            style={{ width: `${getShare(item.value, totalCost)}%` }}
          />
        ))}
      </div>
      <div className="mt-3 grid grid-cols-1 gap-2 text-xs sm:grid-cols-2">
        {items.map((item) => (
          <div key={item.label} className="flex items-center gap-2 text-muted-foreground">
            <span className={cn('h-2 w-2 rounded-sm', barToneStyles[item.tone])} />
            <span className="break-words">{item.label} {getShare(item.value, totalCost).toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CostMixRow({
  label,
  amount,
  share,
  perUser,
}: {
  label: string;
  amount: number;
  share: number;
  perUser: number;
}) {
  return (
    <div className="grid grid-cols-2 gap-2 px-3 py-3 text-xs sm:grid-cols-[minmax(0,1fr)_minmax(5rem,6rem)_minmax(4rem,5rem)_minmax(5rem,6rem)] sm:gap-3">
      <div className="break-words font-medium text-foreground">{label}</div>
      <div className="text-right font-mono tracking-normal text-foreground">{formatCurrency(amount)}</div>
      <div className="text-right font-mono tracking-normal text-muted-foreground">{share.toFixed(1)}%</div>
      <div className="text-right font-mono tracking-normal text-muted-foreground">{formatCurrency(perUser)}/人</div>
    </div>
  );
}

function SignalRow({
  label,
  value,
  detail,
  tone,
}: {
  label: string;
  value: string;
  detail: string;
  tone: Tone;
}) {
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="break-words text-sm font-medium text-foreground">{label}</div>
          <div className="mt-1 break-words text-xs leading-5 text-muted-foreground">{detail}</div>
        </div>
        <div className={cn('break-words text-right font-mono text-lg font-semibold tracking-normal', textToneStyles[tone])}>{value}</div>
      </div>
    </div>
  );
}

function BridgeValue({ label, value, tone }: { label: string; value: string; tone: Tone }) {
  return (
    <div className={cn('min-w-0 rounded-lg border p-3', metricToneStyles[tone])}>
      <div className="break-words text-xs text-muted-foreground">{label}</div>
      <div className={cn('mt-1 break-words font-mono text-base font-semibold tracking-normal', textToneStyles[tone])}>{value}</div>
    </div>
  );
}

function ObservedRevenueMetric({
  label,
  value,
  detail,
  tone,
}: {
  label: string;
  value: string;
  detail: string;
  tone: Tone;
}) {
  return (
    <div className={cn('min-w-0 rounded-lg border p-3', metricToneStyles[tone])}>
      <div className="break-words text-xs font-medium text-muted-foreground">{label}</div>
      <div className={cn('mt-2 break-words font-mono text-lg font-semibold tracking-normal', textToneStyles[tone])}>{value}</div>
      <div className="mt-1 break-words text-xs text-muted-foreground">{detail}</div>
    </div>
  );
}

function ScenarioTrendIcon({ profit }: { profit: number }) {
  return (
    <div className={cn('rounded-lg p-2', profit >= 0 ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive')}>
      {profit >= 0 ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
    </div>
  );
}

function ScenarioMiniMetric({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: Tone;
}) {
  return (
    <div className="min-w-0 rounded-lg bg-card/75 p-2">
      <div className={cn('break-words font-mono text-base font-semibold tracking-normal', textToneStyles[tone])}>{value}</div>
      <div className="mt-0.5 break-words text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function ProjectionItem({
  label,
  value,
  subtitle,
  variant,
}: {
  label: string;
  value: string;
  subtitle?: string;
  variant: Tone;
}) {
  return (
    <div className={cn('min-w-0 rounded-lg border p-3', metricToneStyles[variant])}>
      <div className="break-words text-xs font-medium text-muted-foreground">{label}</div>
      <div className={cn('mt-2 break-words font-mono text-lg font-semibold tracking-normal', textToneStyles[variant])}>{value}</div>
      {subtitle && <div className="mt-1 break-words text-xs text-muted-foreground">{subtitle}</div>}
    </div>
  );
}

const textToneStyles: Record<Tone, string> = {
  neutral: 'text-foreground',
  profit: 'text-success',
  warning: 'text-warning',
  danger: 'text-destructive',
  info: 'text-info',
};

const heroToneStyles: Record<Tone, string> = {
  neutral: 'text-background',
  profit: 'text-success',
  warning: 'text-warning',
  danger: 'text-destructive',
  info: 'text-info',
};

const metricToneStyles: Record<Tone, string> = {
  neutral: 'border-border bg-surface-2',
  profit: 'border-success/25 bg-success/10',
  warning: 'border-warning/25 bg-warning/10',
  danger: 'border-destructive/25 bg-destructive/10',
  info: 'border-info/25 bg-info/10',
};

const pillToneStyles: Record<Tone, string> = {
  neutral: 'border-border bg-surface-2 text-foreground',
  profit: 'border-success/25 bg-success/10 text-success',
  warning: 'border-warning/25 bg-warning/10 text-warning',
  danger: 'border-destructive/25 bg-destructive/10 text-destructive',
  info: 'border-info/25 bg-info/10 text-info',
};

const barToneStyles: Record<Tone, string> = {
  neutral: 'bg-primary',
  profit: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-destructive',
  info: 'bg-info',
};
